import { DragStartEvent, UniqueIdentifier, useDndMonitor, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ReactElement } from 'react';
import { useDragOverlayContext } from '../DragOverlay';
import SortableWidgetWrapper from './SortableWidgetWrapper';

interface DndWrapperProps<T> {
  renderElement: (id: T, isDragged?: boolean) => ReactElement | null;
  items: T[];
  id: string;
}

const SortableElementsList = <T extends UniqueIdentifier>({
  renderElement,
  items,
  id,
}: DndWrapperProps<T>): ReactElement | null => {
  const { showDraggedElement, hideDraggedElement } = useDragOverlayContext();
  const { setNodeRef } = useDroppable({ id });

  useDndMonitor({
    onDragStart({ active }: DragStartEvent) {
      const element = renderElement(active.id as T, true);
      if (element) {
        showDraggedElement(element);
      }
    },
    onDragEnd() {
      hideDraggedElement();
    },
  });

  if (!items) {
    return null;
  }

  const isDragButtonDisabled = items.length === 1;
  return (
    <SortableContext items={items} strategy={verticalListSortingStrategy} id={id}>
      <div ref={setNodeRef} data-testid={`${id}-droppable`}>
        {items.map((item) => (
          <SortableWidgetWrapper id={item} key={item} isDragButtonDisabled={isDragButtonDisabled}>
            {renderElement(item)}
          </SortableWidgetWrapper>
        ))}
      </div>
    </SortableContext>
  );
};

export default SortableElementsList;
